

// Also export the CotizacionesForm component for completeness
import { Form, Input, Button, InputNumber, Space, Divider, Modal, DatePicker, AutoComplete, Row, Col, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import axios from "axios"; // Already imported above
import { useEffect, useState } from "react"; // Already imported above
import moment from "moment"; // Already imported above

export default function CotizacionesForm({ visible, onCancel, setCotizacionesList, record, isNew }) {
  const [form] = Form.useForm();
  const [arancelesData, setArancelesData] = useState([]);
  const [asentamientos, setAsentamientos] = useState([]);

  const tensionOptions = [{ value: "Baja" }, { value: "Media" }, { value: "Baja y Media" }, { value: "Otra" }];

  const servicioOptions = [{ value: "Residencial" }, { value: "Comercial" }, { value: "Industrial" }];

  const handleCloseCotizacionesForm = () => {
    form.resetFields();
    setAsentamientos([]);
    onCancel();
  };

  const handleCotizacionSubmit = () => {
    if (isNew) {
      handleCotizacionCreate();
    } else {
      handleCotizacionUpdate();
    }
  };

  const handleCotizacionUpdate = () => {
    form
      .validateFields()
      .then((values) => {
        const payload = {
          ...values,
          FechaCotizacion: moment(values.FechaCotizacion).format("YYYY-MM-DD"),
        };

        return axios.put(`${process.env.REACT_APP_API_URL}/cotizaciones/${record.CotizacionID}`, payload);
      })
      .then(() => {
        return axios.get(`${process.env.REACT_APP_API_URL}/cotizaciones`);
      })
      .then((res) => {
        setCotizacionesList(res.data);
        message.success("Cotización actualizada correctamente");
        handleCloseCotizacionesForm();
      })
      .catch((err) => {
        console.error("Error updating cotización:", err);
        message.error("Error al actualizar la cotización");
      });
  };

  const handleCotizacionCreate = () => {
    form
      .validateFields()
      .then((values) => {
        const payload = {
          ...values,
          FechaCotizacion: moment(values.FechaCotizacion).format("YYYY-MM-DD"),
        };

        return axios.post(`${process.env.REACT_APP_API_URL}/cotizaciones`, payload);
      })
      .then(() => {
        return axios.get(`${process.env.REACT_APP_API_URL}/cotizaciones`);
      })
      .then((res) => {
        setCotizacionesList(res.data);
        message.success("Cotización creada correctamente");
        handleCloseCotizacionesForm();
      })
      .catch((err) => {
        console.error("Error creating cotización:", err);
        message.error("Error al crear la cotización");
      });
  };

  const fetchAsentamientos = async (codigoPostal) => {
    try {
      const res = await axios.get(`https://codigospostalesmx-94e44-default-rtdb.firebaseio.com/datos/${codigoPostal}.json`);

      if (res.data !== null) {
        setAsentamientos(res.data.colonias || []);
        return res.data;
      } else {
        setAsentamientos([]);
        return null;
      }
    } catch (err) {
      console.error("Error fetching asentamientos:", err);
      message.error("Error al cargar los asentamientos para el código postal");
      setAsentamientos([]);
      return null;
    }
  };

  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        ...record,
        FechaCotizacion: moment(record.FechaCotizacion),
      });

      if (record.CodigoPostal) {
        fetchAsentamientos(record.CodigoPostal);
      }
    } else {
      // Set default values for new cotización
      form.setFieldsValue({
        FechaCotizacion: moment(),
        TipoTension: "Baja",
        TipoServicio: "Residencial",
        CargaInstalada: 10,
        PrecioFinal: 1200,
      });
      setAsentamientos([]);
    }
  }, [record, form]);

  useEffect(() => {
    const fetchAranceles = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/utils/wildcard-aranceles`);
        setArancelesData(res.data || []);
      } catch (err) {
        console.error("Error fetching aranceles:", err);
        message.error("Error al cargar datos de aranceles");
      }
    };

    fetchAranceles();
  }, []);

  const requiredFieldProps = {
    rules: [{ required: true, message: "Campo obligatorio" }],
  };

  const onBlur = async (e) => {
    const codigoPostal = e.target.value;

    if (codigoPostal.length === 5) {
      const data = await fetchAsentamientos(codigoPostal);

      if (data) {
        form.setFieldsValue({
          Municipio: data.municipio || "",
          Estado: data.estado || "",
          Colonia: null,
        });
      } else {
        form.setFieldsValue({
          Colonia: null,
          Municipio: null,
          Estado: null,
        });
        message.error("No se encontró el código postal");
      }
    }
  };

  const getPrecioFinalFromCargaInstalada = (cargaInstalada) => {
    if (!arancelesData || arancelesData.length === 0 || cargaInstalada == null) {
      return null;
    }

    const matchingArancel = [...arancelesData].reverse().find((arancel) => arancel[0] <= cargaInstalada);

    return matchingArancel ? matchingArancel[1] : null;
  };

  const handleCargaInstalladaChange = (value) => {
    const finalPrice = getPrecioFinalFromCargaInstalada(value);
    if (finalPrice !== null) {
      form.setFieldsValue({ PrecioFinal: finalPrice });
    }
  };

  return (
    <Modal title="Datos de la cotización" visible={visible} onCancel={handleCloseCotizacionesForm} footer={null} width={800} destroyOnClose>
      <Form form={form} layout="vertical" name="cotizacionesForm" onFinish={handleCotizacionSubmit}>
        <Divider orientation="left">Información General</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Solicitante" name="Solicitante" {...requiredFieldProps}>
              <Input
                placeholder="Nombre del solicitante"
                onChange={(e) => form.setFieldsValue({ Solicitante: e.target.value })}
                value={form.getFieldValue('Solicitante')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Fecha de cotización" name="FechaCotizacion" {...requiredFieldProps}>
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                value={form.getFieldValue('FechaCotizacion')}
                onChange={(date) => form.setFieldsValue({ FechaCotizacion: date })}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Detalles del Servicio</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Tipo de tensión" name="TipoTension" {...requiredFieldProps}>
              <AutoComplete
                options={tensionOptions}
                placeholder="Selecciona tipo de tensión"
                style={{ width: "100%" }}
                onSelect={(value) => form.setFieldsValue({ TipoTension: value })}
                value={form.getFieldValue('TipoTension')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Tipo de servicio" name="TipoServicio" {...requiredFieldProps}>
              <AutoComplete
                options={servicioOptions}
                placeholder="Selecciona tipo de servicio"
                style={{ width: "100%" }}
                onSelect={(value) => form.setFieldsValue({ TipoServicio: value })}
                value={form.getFieldValue('TipoServicio')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Carga instalada" name="CargaInstalada" {...requiredFieldProps}>
              <InputNumber
                addonAfter="kW"
                onChange={handleCargaInstalladaChange}
                style={{ width: "100%" }}
                min={0}
                placeholder="0"
                value={form.getFieldValue('CargaInstalada')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Precio final" name="PrecioFinal" {...requiredFieldProps}>
              <InputNumber
                addonBefore="$"
                style={{ width: "100%" }}
                min={0}
                placeholder="0"
                onChange={(value) => form.setFieldsValue({ PrecioFinal: value })}
                value={form.getFieldValue('PrecioFinal')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Información de Domicilio</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Código postal" name="CodigoPostal" {...requiredFieldProps}>
              <Input
                onBlur={onBlur}
                placeholder="12345"
                maxLength={5}
                onChange={(e) => form.setFieldsValue({ CodigoPostal: e.target.value })}
                value={form.getFieldValue('CodigoPostal')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Colonia" name="Colonia" {...requiredFieldProps}>
              <AutoComplete
                options={asentamientos.map((asentamiento) => ({ value: asentamiento }))}
                placeholder="Selecciona una colonia"
                onSelect={(value) => form.setFieldsValue({ Colonia: value })}
                onChange={(value) => form.setFieldsValue({ Colonia: value })}
                value={form.getFieldValue('Colonia')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Calle" name="Calle" {...requiredFieldProps}>
              <Input
                placeholder="Nombre de la calle"
                onChange={(e) => form.setFieldsValue({ Calle: e.target.value })}
                value={form.getFieldValue('Calle')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Número exterior" name="NumeroExterior" {...requiredFieldProps}>
              <Input
                placeholder="123"
                onChange={(e) => form.setFieldsValue({ NumeroExterior: e.target.value })}
                value={form.getFieldValue('NumeroExterior')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Número interior" name="NumeroInterior">
              <Input
                placeholder="A, B, 101, etc."
                onChange={(e) => form.setFieldsValue({ NumeroInterior: e.target.value })}
                value={form.getFieldValue('NumeroInterior')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Entre calle 1" name="EntreCalle1">
              <Input
                placeholder="Primera calle de referencia"
                onChange={(e) => form.setFieldsValue({ EntreCalle1: e.target.value })}
                value={form.getFieldValue('EntreCalle1')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Entre calle 2" name="EntreCalle2">
              <Input
                placeholder="Segunda calle de referencia"
                onChange={(e) => form.setFieldsValue({ EntreCalle2: e.target.value })}
                value={form.getFieldValue('EntreCalle2')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Municipio" name="Municipio" {...requiredFieldProps}>
              <Input placeholder="Municipio" readOnly />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Estado" name="Estado" {...requiredFieldProps}>
              <Input placeholder="Estado" readOnly />
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Space direction="horizontal">
            <Button type="default" onClick={handleCloseCotizacionesForm}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit">
              {isNew ? "Crear" : "Actualizar"}
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
}
